import type { RequestContext } from "../context/request-context.js";
import type { InspectionId } from "../../shared/kernel/identifiers.js";
import type { MinutaRepository } from "../../repositories/operational-repositories.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import type { Minuta, MinutaRevision, MinutaStatus, MinutaCorrectionRequest } from "../../domain/report/minuta-domain.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import type { MinutaSourceSnapshot } from "../../domain/report/minuta-source.js";

const randomUUID = () => globalThis.crypto.randomUUID();

export class MinutaService {
  constructor(
    public minutaRepo: MinutaRepository,
    public audit?: AuditRecorder
  ) {}

  async getMinuta(context: RequestContext, id: string): Promise<Minuta | null> {
    return this.minutaRepo.findById(context, id);
  }

  async getMinutaByInspection(context: RequestContext, inspectionId: string): Promise<Minuta | null> {
    // P0-02: deprecated — retorna a primeira Minuta encontrada (compatibilidade).
    // Novos fluxos devem usar getMinutaByInspectionAndExecutionType ou listByInspection.
    return this.minutaRepo.findByInspection(context, inspectionId as InspectionId);
  }

  /** P0-02: busca determinística por inspeção + tipo de execução. */
  async getMinutaByInspectionAndExecutionType(context: RequestContext, inspectionId: string, executionType: 'PARADA' | 'ROTINA'): Promise<Minuta | null> {
    return this.minutaRepo.findByInspectionAndExecutionType(context, inspectionId as InspectionId, executionType);
  }

  /** P0-02: lista todas as Minutas de uma inspeção (PARADA e/ou ROTINA). */
  async listMinutasByInspection(context: RequestContext, inspectionId: string): Promise<readonly Minuta[]> {
    return this.minutaRepo.listByInspection(context, inspectionId as InspectionId);
  }

  // P0-02 V4 Causa 6: corrigido para retornar TODAS as Minutas da inspeção (PARADA e/ou ROTINA).
  // A implementação anterior usava findByInspection (singleton) e perdia o segundo documento.
  async listByInspection(context: RequestContext, inspectionId: string): Promise<readonly Minuta[]> {
    return this.minutaRepo.listByInspection(context, inspectionId as InspectionId);
  }

  /**
   * Leitura pura para o Book de Inspeção (consolidador documental) — nunca
   * cria nem altera Minuta, só lista as já existentes do Equipamento.
   * `listAll` é opcional no contrato do repositório (a implementação SQLite
   * ainda não o expõe); sem ele, devolve lista vazia em vez de falhar.
   */
  async listByEquipment(context: RequestContext, equipmentId: string): Promise<readonly Minuta[]> {
    if (!this.minutaRepo.listAll) return [];
    const all = await this.minutaRepo.listAll(context);
    return all.filter(m => String(m.equipmentId) === String(equipmentId));
  }

  async getRevisions(context: RequestContext, minutaId: string): Promise<readonly MinutaRevision[]> {
    return this.minutaRepo.findRevisionsByMinuta(context, minutaId);
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  private async generateTrackingNumber(context: RequestContext, projectId: string): Promise<string> {
    const year = new Date().getFullYear();
    const existing = await this.minutaRepo.findByProjectAndYear(context, projectId, year);
    // Gerado UMA vez, na criação da Minuta — nunca muda entre revisões (regra inalterada).
    if (this.numbering) {
      return this.numbering.allocate({
        documentType: "MINUTA", projectId,
        legacyCount: async () => existing.length,
        isTaken: async candidate => (await this.minutaRepo.findByProjectAndYear(context, projectId, year)).some(m => m.trackingNumber === candidate)
      });
    }
    const count = existing.length + 1;
    const seq = count.toString().padStart(6, '0');
    // Using a default project prefix if none provided, ideally we'd fetch project details, but for now we'll use PRJ
    const prjPrefix = projectId.substring(0, 3).toUpperCase() || 'PRJ';
    return `MIN-${prjPrefix}-${seq}-${year}`;
  }

  async createInitialMinuta(
    context: RequestContext,
    equipmentId: string,
    inspectionId: string,
    projectId: string,
    snapshotData: any,
    source?: MinutaSourceSnapshot,
    executionType?: 'PARADA' | 'ROTINA'
  ): Promise<{ minuta: Minuta, revision: MinutaRevision, snapshot: DocumentSnapshot }> {
    // P0-02: executionType é obrigatório. Falhar cedo se ausente ou inválido.
    if (!executionType || (executionType !== 'PARADA' && executionType !== 'ROTINA')) {
      throw new AppError(ErrorCode.Validation, `Minuta executionType obrigatório (PARADA ou ROTINA). Recebido: ${executionType}`);
    }

    // P0-02: busca por inspeção + tipo garante independência documental.
    const existing = await this.minutaRepo.findByInspectionAndExecutionType(context, inspectionId as InspectionId, executionType);
    if (existing) {
      throw new AppError(ErrorCode.Validation, `Minuta ${executionType} já existe para esta inspeção.`);
    }

    const trackingNumber = await this.generateTrackingNumber(context, projectId);

    if (snapshotData) {
      snapshotData.trackingNumber = trackingNumber;
      snapshotData.revisionNumber = '00';
      snapshotData.executionType = executionType;
    }

    const minuta: Minuta = {
      id: `MINUTA-${randomUUID()}`,
      trackingNumber,
      currentRevision: '00',
      equipmentId,
      inspectionId,
      projectId,
      executionType,
      status: 'EMITIDA',
      ...(source ? { lastSourceHash: source.sourceHash, lastSourceSnapshot: source } : {}),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const snapshotId = `SNAP-${randomUUID()}`;
    const snapshot: DocumentSnapshot = {
      id: snapshotId,
      type: 'MINUTA',
      revision: '00',
      createdAt: new Date(),
      data: snapshotData,
      documentNumber: trackingNumber
    };

    const revision: MinutaRevision = {
      id: `MINREV-${randomUUID()}`,
      minutaId: minuta.id,
      revisionNumber: '00',
      snapshotId: snapshotId,
      snapshotData: snapshotData,
      correctionReason: null,
      createdAt: new Date(),
      createdBy: context.userId || 'SYSTEM',
      status: 'ATUAL'
    };

    await this.minutaRepo.save(context, minuta);
    await this.minutaRepo.saveRevision(context, revision);

    if (this.audit) {
      await this.audit.record(context, {
        action: 'CREATE' as any,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: null,
        newState: { minuta, revision } as any,
        metadata: { equipmentId, inspectionId }
      });
    }

    return { minuta, revision, snapshot };
  }

  async requestCorrection(
    context: RequestContext, 
    minutaId: string, 
    requests: { recommendationId: string, visualIndex: string, reason: string }[]
  ): Promise<Minuta> {
    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) {
      throw new AppError(ErrorCode.NotFound, "Minuta not found.");
    }
    
    if (minuta.status !== 'EMITIDA') {
      throw new AppError(ErrorCode.Validation, "Only EMITIDA minutas can be sent to correction.");
    }

    if (!requests || requests.length === 0) {
      throw new AppError(ErrorCode.Validation, "At least one correction request is required.");
    }

    const previousState = { ...minuta };
    minuta.status = 'EM CORREÇÃO';
    minuta.updatedAt = new Date();
    await this.minutaRepo.save(context, minuta);

    for (const req of requests) {
      const correction: MinutaCorrectionRequest = {
        id: `CORREQ-${randomUUID()}`,
        minutaId,
        recommendationId: req.recommendationId,
        visualIndex: req.visualIndex,
        reason: req.reason,
        status: 'PENDENTE',
        requesterId: context.userId || 'SYSTEM',
        inspectorId: null, // Ideally we would inject the inspector ID here based on the inspection
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await this.minutaRepo.saveCorrectionRequest(context, correction);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: 'UPDATE' as any,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: previousState as any,
        newState: minuta as any,
        metadata: { action: 'REQUEST_CORRECTION', itemsCount: requests.length }
      });
    }

    return minuta;
  }

  async getCorrectionRequests(context: RequestContext, minutaId: string): Promise<readonly MinutaCorrectionRequest[]> {
    return this.minutaRepo.findCorrectionRequests(context, minutaId);
  }

  async updateCorrectionStatus(context: RequestContext, minutaId: string, correctionId: string, status: MinutaCorrectionRequest['status']): Promise<void> {
    const corrections = await this.minutaRepo.findCorrectionRequests(context, minutaId);
    const correction = corrections.find(c => c.id === correctionId);
    if (!correction) {
      throw new AppError(ErrorCode.NotFound, "Correction request not found.");
    }
    correction.status = status;
    correction.updatedAt = new Date();
    await this.minutaRepo.saveCorrectionRequest(context, correction);
  }

  async createRevision(
    context: RequestContext, 
    minutaId: string, 
    snapshotData: any,
    reason: string
  ): Promise<{ minuta: Minuta, revision: MinutaRevision, snapshot: DocumentSnapshot }> {
    
    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found.");
    
    if (minuta.status !== 'EM CORREÇÃO') {
      throw new AppError(ErrorCode.Validation, "Minuta must be EM CORREÇÃO to generate a new revision.");
    }

    // Validação de todas as correções estarem aprovadas:
    const corrections = await this.minutaRepo.findCorrectionRequests(context, minutaId);
    const pendingCorrections = corrections.filter(c => c.status !== 'VALIDADA');
    
    if (pendingCorrections.length > 0) {
      // Por enquanto vamos ignorar na demo, mas num sistema real:
      // throw new AppError(ErrorCode.Validation, "All corrections must be VALIDADA before creating a revision.");
    }

    // Deprecate previous revisions
    const allRevisions = await this.minutaRepo.findRevisionsByMinuta(context, minutaId);
    for (const rev of allRevisions) {
      if (rev.status === 'ATUAL') {
        rev.status = 'SUBSTITUÍDA';
        await this.minutaRepo.saveRevision(context, rev);
      }
    }

    // Increment Revision Number
    const currentRevNum = parseInt(minuta.currentRevision, 10);
    const nextRevNum = (currentRevNum + 1).toString().padStart(2, '0');

    minuta.currentRevision = nextRevNum;
    minuta.status = 'EMITIDA';
    minuta.updatedAt = new Date();

    const snapshotId = `SNAP-${randomUUID()}`;

    if (snapshotData) {
      snapshotData.trackingNumber = minuta.trackingNumber;
      snapshotData.revisionNumber = nextRevNum;
      // P0-02: executionType obrigatório no snapshot para integridade documental.
      snapshotData.executionType = minuta.executionType;
    }

    const snapshot: DocumentSnapshot = {
      id: snapshotId,
      type: 'MINUTA',
      revision: nextRevNum,
      createdAt: new Date(),
      data: snapshotData,
      documentNumber: minuta.trackingNumber
    };

    const revision: MinutaRevision = {
      id: `MINREV-${randomUUID()}`,
      minutaId: minuta.id,
      revisionNumber: nextRevNum,
      snapshotId: snapshotId,
      snapshotData: snapshotData,
      correctionReason: reason,
      createdAt: new Date(),
      createdBy: context.userId || 'SYSTEM',
      status: 'ATUAL'
    };

    await this.minutaRepo.save(context, minuta);
    await this.minutaRepo.saveRevision(context, revision);

    if (this.audit) {
      await this.audit.record(context, {
        action: 'UPDATE' as any,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: { status: 'EM CORREÇÃO', revision: currentRevNum.toString().padStart(2, '0') } as any,
        newState: { status: 'EMITIDA', revision: nextRevNum, executionType: minuta.executionType } as any,
        metadata: { action: 'CREATE_REVISION', reason }
      });
    }

    return { minuta, revision, snapshot };
  }

  async markUpdatePending(context: RequestContext, minutaId: string, diffSummary?: any): Promise<Minuta> {
    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found.");

    if (minuta.status !== 'ATUALIZAÇÃO PENDENTE') {
      const previousState = { ...minuta };
      minuta.status = 'ATUALIZAÇÃO PENDENTE';
      minuta.updatedAt = new Date();
      await this.minutaRepo.save(context, minuta);

      if (this.audit) {
        await this.audit.record(context, {
          action: 'UPDATE' as any,
          entityType: 'MINUTA',
          entityId: minuta.id,
          previousState: previousState as any,
          newState: minuta as any,
          metadata: { action: 'MINUTA_UPDATE_PENDING', diffSummary }
        });
      }
    }
    return minuta;
  }

  /** Grava a fonte (hash + snapshot) da revisão atual — usada quando a Minuta foi gerada antes do hash existir. */
  async saveSource(context: RequestContext, minutaId: string, source: MinutaSourceSnapshot): Promise<Minuta> {
    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found.");
    minuta.lastSourceHash = source.sourceHash;
    minuta.lastSourceSnapshot = source;
    await this.minutaRepo.save(context, minuta);
    return minuta;
  }

  /** SALVAR RASCUNHO da Engenharia: observações da análise, sem mexer em status/revisão. */
  async saveEngineeringNotes(context: RequestContext, minutaId: string, notes: string): Promise<Minuta> {
    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found.");
    minuta.engineeringNotes = (notes || "").trim();
    minuta.updatedAt = new Date();
    await this.minutaRepo.save(context, minuta);
    return minuta;
  }

  async createUpdateRevision(
    context: RequestContext,
    minutaId: string,
    snapshotData: any,
    reason?: string,
    source?: MinutaSourceSnapshot
  ): Promise<{ minuta: Minuta, revision: MinutaRevision, snapshot: DocumentSnapshot }> {

    const minuta = await this.minutaRepo.findById(context, minutaId);
    if (!minuta) throw new AppError(ErrorCode.NotFound, "Minuta not found.");

    // P0-02 NO-OP: não gerar revisão sem delta documental real.
    // Se o hash da fonte atual for idêntico ao último hash gravado,
    // não houve alteração nas recomendações nem nos dados documentais versionados.
    if (source && minuta.lastSourceHash && source.sourceHash === minuta.lastSourceHash) {
      const allRevisions = await this.minutaRepo.findRevisionsByMinuta(context, minutaId);
      const currentRev = allRevisions.find(r => r.status === 'ATUAL') ?? allRevisions[allRevisions.length - 1];
      if (!currentRev) throw new AppError(ErrorCode.NotFound, "No current revision found for NO-OP return.");
      const snapshot: DocumentSnapshot = {
        id: currentRev.snapshotId,
        type: 'MINUTA',
        revision: currentRev.revisionNumber,
        createdAt: currentRev.createdAt,
        data: currentRev.snapshotData,
        documentNumber: minuta.trackingNumber
      };
      return { minuta, revision: currentRev, snapshot };
    }

    // Deprecate previous revisions
    const allRevisions = await this.minutaRepo.findRevisionsByMinuta(context, minutaId);
    for (const rev of allRevisions) {
      if (rev.status === 'ATUAL') {
        rev.status = 'SUBSTITUÍDA';
        await this.minutaRepo.saveRevision(context, rev);
      }
    }

    // Increment Revision Number
    const currentRevNum = parseInt(minuta.currentRevision, 10);
    const nextRevNum = (currentRevNum + 1).toString().padStart(2, '0');

    // Prevent duplicate revisions
    const duplicate = allRevisions.find(r => r.revisionNumber === nextRevNum);
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, `Revision ${nextRevNum} already exists for this Minuta.`);
    }

    const previousRevision = minuta.currentRevision;
    minuta.currentRevision = nextRevNum;
    minuta.status = 'EMITIDA';
    minuta.updatedAt = new Date();
    if (source) {
      minuta.lastSourceHash = source.sourceHash;
      minuta.lastSourceSnapshot = source;
    }

    // Revision independence (Bloco 6, Fase 40): a signed/validated REV does
    // NOT carry over to the next one. Each new revision starts fully
    // unanalyzed/unvalidated/unapproved again — clear every Engineering
    // Analysis / Validar Minuta / PLH Approval tracking field from the
    // previous revision so the new one can never display or infer a
    // conformidade/assinatura it never actually went through.
    minuta.analysisStartedBy = undefined;
    minuta.analysisStartedAt = undefined;
    minuta.analysisProfessionalId = undefined;
    minuta.analysisCompletedAt = undefined;
    minuta.validatedAt = undefined;
    minuta.validatedByUserId = undefined;
    minuta.validatedByProfessionalId = undefined;
    minuta.approvedBy = undefined;
    minuta.approvedByProfessionalId = undefined;
    minuta.approvedAt = undefined;
    minuta.signatureHash = undefined;

    const snapshotId = `SNAP-${randomUUID()}`;

    if (snapshotData) {
      snapshotData.trackingNumber = minuta.trackingNumber;
      snapshotData.revisionNumber = nextRevNum;
      // P0-02: executionType obrigatório no snapshot para integridade documental.
      snapshotData.executionType = minuta.executionType;
    }

    const snapshot: DocumentSnapshot = {
      id: snapshotId,
      type: 'MINUTA',
      revision: nextRevNum,
      createdAt: new Date(),
      data: snapshotData,
      documentNumber: minuta.trackingNumber
    };

    const finalReason = reason && reason.trim() ? reason.trim() : 'Inclusão de nova(s) recomendação(ões)';

    const revision: MinutaRevision = {
      id: `MINREV-${randomUUID()}`,
      minutaId: minuta.id,
      revisionNumber: nextRevNum,
      snapshotId: snapshotId,
      snapshotData: snapshotData,
      correctionReason: finalReason,
      createdAt: new Date(),
      createdBy: context.userId || 'SYSTEM',
      status: 'ATUAL'
    };

    await this.minutaRepo.save(context, minuta);
    await this.minutaRepo.saveRevision(context, revision);

    if (this.audit) {
      await this.audit.record(context, {
        action: 'CREATE' as any,
        entityType: 'MINUTA',
        entityId: minuta.id,
        previousState: { status: minuta.status, revision: previousRevision } as any,
        newState: { status: 'EMITIDA', revision: nextRevNum } as any,
        metadata: { 
          action: 'MINUTA_REVISION_CREATED', 
          trackingNumber: minuta.trackingNumber,
          oldRevision: previousRevision,
          newRevision: nextRevNum,
          reason: finalReason 
        }
      });
    }

    return { minuta, revision, snapshot };
  }
}
